import { BALANCE_INITIAL_STATE } from 'context/reducers/constants'
import { ICompanyBalanceAmount } from 'context/reducers/interface'
import { TFunction } from 'i18next'

const clabe = require('clabe-js')

export const REGEX: { [index: string]: any } = {
  alphaNumericDashOrUnderscore: /[^\w\s_\-\u00F1\u00D1\u00C0-\u017F]/,
  clean: /^[^`´~!@#$€%^*()+=[{\]}|\\'<>?";]+$/,
  digits: /^[0-9]*$/,
  email: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{1,12}$/i,
  emailWithDomain: /^[a-zA-Z0-9_.+-]+@(?!(?:hotmail|gmail|conekta|outlook|yahoo)\.com\b)[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
  text: /^[a-zA-Z\s\u00F1\u00D1\u00C0-\u017F]*$/i,
  // tslint:disable-next-line: max-line-length
  fullName:
    /^ *[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆŠŽ∂ð,.'-]+ [a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆŠŽ∂ð, .'-]+$/u,
  // tslint:disable-next-line: max-line-length
  taxPayerRegister:
    /^([A-ZÑ&]{3,4}) ?(?:- ?)?(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])) ?(?:- ?)?([A-Z\d]{2})([A\d])$/i,
  // tslint:disable-next-line: max-line-length
  url: /^(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})$/i,
  wholeWord: /[A-Z]{2}/i,
  metadata: {
    key: /^[0-9a-zA-Z_\s\-\[\]\{\}\|\\\,ñÑÃáàâäåéèêëíìïîóòôöÕúùüûÝÿÁÉÍÓÚÀÈÌÒÙ()+!:%&;:?¿*$/^ª·¨¢Ç#@¡…º<>«»–_‘`~æ£¥≡°∙·²€‚„ˆ‰‹›“•˜™šž¨©®³""'´¸ ]+$/,
    value:
      /^[0-9a-zA-Z_\s\.\-\[\]\{\}\|\\\,ñÑÃáàâäåéèêëíìïîóòôöÕúùüûÝÿÁÉÍÓÚÀÈÌÒÙ()+!:%&;:?¿*$/^ª·¨¢Ç#@¡…º<>«»–_‘`~æ£¥≡°∙·²€‚„ˆ‰‹›“•˜™šž¨©®³""'´¸ ]+$/
  },
  productDescription:
    /^[0-9a-zA-Z_\s\.\-\[\]\{\}\|\\\,ñÑÃáàâäåéèêëíìïîóòôöÕúùüûÝÿÁÉÍÓÚÀÈÌÒÙ()+!:%&;:?¿*$/^ª·¨¢Ç#@¡…º<>«»_–―‘`~æ£¥≡°∙·²€‚„ˆ‰‹›“•˜™šž¨©®³""'´¸ ]+$/,
  empty: /^(?!\s*$).+/,
  startHttp: /^(http|https):\/\//i,
  isAlphanumeric24: /^(?=[A-Za-z0-9]{24}$)/,
  domain: /^@[A-Z0-9.-]+\.[A-Z]{1,12}$/i,
  companyId: /^(?=[A-Za-z0-9]{24,32}$)/
}

const T_ROOT = 'form.errors'

export const composeValidators =
  (...validators: any) =>
  (value: any, allValues: any) =>
    validators.reduce((error: any, validator: any) => error || validator(value, allValues), undefined)

const errMsg = (message: string, t: any, value: any = '') => `${t(`${T_ROOT}.${message}`)}${value}`

const evaluate = (evaluationResult: boolean, errorMessage: string) => (evaluationResult ? errorMessage : undefined)

export const preventCopyPaste = (e: KeyboardEvent) => {
  e.preventDefault()
  return false
}

// EVALUATION FUNCTIONS ························································
export const equalNumberOfChars = (t: any, length: number) => (value: any) =>
  evaluate(value ? value.length !== length : false, errMsg('equalNumberOfChars', t, length))

export const hasMaxChars = (t: any, max: number) => (value: any) =>
  evaluate(value ? value.length > max : false, errMsg('hasMaxChars', t, max))

export const hasMinChars = (t: any, min: number) => (value: any) =>
  evaluate(value ? value.length < min : false, errMsg('hasMinChars', t, min))

export const hasOnlyDigits = (t: any) => (value: any) =>
  evaluate(value ? !REGEX.digits.test(value) : false, errMsg('hasOnlyDigits', t))

export const hasOnlyText = (t: any) => (value: any) =>
  evaluate(value ? !REGEX.text.test(value) : false, errMsg('hasOnlyText', t))

export const hasOnlyTextShort = (t: any) => (value: any) =>
  evaluate(value ? !REGEX.text.test(value) : false, errMsg('hasOnlyTextShort', t))

export const hasWholeWord = (t: TFunction) => (value: string) =>
  evaluate(value ? !REGEX.wholeWord.test(value) : false, errMsg('hasWholeWord', t))

export const isAlphanumericDashOrUnderscore = (t: any) => (value: any) =>
  evaluate(value ? REGEX.alphaNumericDashOrUnderscore.test(value) : false, errMsg('isAlphanumericDashOrUnderscore', t))

export const isCleanText = (t: any) => (value: any) =>
  evaluate(value ? !REGEX.clean.test(value) : false, errMsg('isCleanText', t))

export const isProductDescription = (t: any) => (value: any) =>
  evaluate(value ? !REGEX.productDescription.test(value) : false, errMsg('isCleanText', t))

export const isExactMatchKeyword = (t: any, keyword: string) => (value: any) =>
  evaluate(value ? value !== keyword : false, errMsg('isExactMatchKeyword', t, keyword))

export const isFullName = (t: any) => (value: any) =>
  evaluate(value ? !REGEX.fullName.test(value) : false, errMsg('isFullName', t))

export const isMaxValue = (t: any, max: number) => (value: any) =>
  evaluate(value ? value > max : false, errMsg('isMaxValue', t, max))

export const isMinValue = (t: any, min: number) => (value: number) =>
  evaluate(value ? value < min : false, errMsg('isMinValue', t, min))

export const isNumber = (t: any) => (value: any) => evaluate(value ? isNaN(value) : false, errMsg('isNumber', t))

export const isRequired = (t: any) => (value: any) => value ? undefined : errMsg('isRequired', t)

export const isNotEmpty = (t: any) => (value: any) =>
  evaluate(value ? !REGEX.empty.test(value) : false, errMsg('isRequired', t))

export const isValidClabe = (t: any) => (value: any) =>
  evaluate(value ? !clabe.validateClabe(value) : false, errMsg('isValidClabe', t))

export const isValidEmail = (t: any) => (value: any) =>
  evaluate(value ? !REGEX.email.test(value) : false, errMsg('isValidEmail', t))

export const isValidEmailWithDomain = (t: TFunction) => (value: string) =>
  evaluate(value ? !REGEX.emailWithDomain.test(value) : false, errMsg('isValidEmailWithDomain', t))

export const isEmailValid = (email: string): boolean => {
  return REGEX.email.test(email)
}

export const isValidTaxPayerRegister = (t: any) => (value: any) =>
  evaluate(value ? !REGEX.taxPayerRegister.test(value) : false, errMsg('isValidTaxPayerRegister', t))

export const isValidUrl = (t: any) => (value: any) =>
  evaluate(value ? !REGEX.url.test(value) : false, errMsg('isValidUrl', t))

export const startWithHttp = (t: any) => (value: any) =>
  evaluate(value ? !REGEX.startHttp.test(value) : false, errMsg('startWithHttp', t))

export const isCleanTextPaymentLinkMetaData = (field: string, t: any) => (value: any) =>
  evaluate(value ? !REGEX.metadata[field].test(value) : false, errMsg('isCleanText', t))

export const isMinMonthExp = (t: any, minMonth: number, currentYear: string) => (value: any) => {
  const expirationMonth = parseInt(value?.slice(0, 2))
  const expirationYear = value?.slice(2, 4)

  return evaluate(
    expirationYear === currentYear ? expirationMonth < minMonth : false,
    errMsg('isMinValueExp', t, minMonth)
  )
}

export const isMaxValueMonthExp = (t: any, max: number) => (value: any) => {
  const expirationMonth = parseInt(value?.slice(0, 2))
  return evaluate(value ? (expirationMonth ? expirationMonth > max : false) : false, errMsg('isMaxValueExp', t, max))
}

export const isMinValueMonthExp = (t: any) => (value: any) =>
  evaluate(value?.slice(0, 2) === '00', errMsg('isMinValueMonthExp', t))

export const isMinValueYearExp = (t: any, min: number) => (value: any) => {
  const expirationYear = parseInt(value?.slice(3, 5))
  return evaluate(value ? (expirationYear ? expirationYear < min : true) : false, errMsg('isMinValueYearExp', t, min))
}

export const isRequiredIf = (t: any, input: string) => (value: any, allValues: any) =>
  evaluate(allValues[input] !== undefined ? value === undefined : false, errMsg('isRequired', t))

export const validateArray = (array: Array<ICompanyBalanceAmount>): number =>
  array?.length > 0 ? array[0]?.amount : BALANCE_INITIAL_STATE.amount

export const isValidDomain =
  (t: TFunction) =>
  (value: string): string | undefined =>
    evaluate(value ? !REGEX.domain.test(value) : false, errMsg('isValidDomain', t))
